import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import useImageSupportsWebP from '@hooks/images/useImageSupportsWebP';
import headerPlaceholder from '@assets/images/loading-header.png';
import getWebPSource from '@utils/images/getWebPSource';
import getDoubleDpiSrc from '@utils/images/getDoubleDpiSrc';

const StyledImage = styled.img`
  object-fit: cover;
  position: absolute;
  top: 0;
  height: ${({ height }) => height};
  width: 100%;
  ${({ isClosed: withOpacity }) =>
    withOpacity &&
    css`
      opacity: 0.5;
    `}
`;

function BackgroundImage({ src, isClosed, height, className }) {
  const [isShowingError, setIsShowingError] = useState(false);
  const [imgLoaded, setImageLoaded] = useState(false);

  const toggleIsShowingError = useCallback(() => setIsShowingError(true), []);
  const onImgLoad = useCallback(() => setImageLoaded(true), []);

  const imageSupportsWebP = useImageSupportsWebP({ src });

  if (isShowingError || !src)
    return (
      <StyledImage
        src={headerPlaceholder}
        isClosed={isClosed}
        height={height}
      />
    );

  return (
    <>
      {!imgLoaded && (
        <StyledImage
          src={headerPlaceholder}
          isClosed={isClosed}
          height={height}
        />
      )}

      <picture className={className}>
        {imageSupportsWebP && (
          <source
            type="image/webp"
            srcSet={getDoubleDpiSrc(getWebPSource(src))}
            height={height}
          />
        )}
        <StyledImage
          src={src}
          imgLoaded={imgLoaded}
          onLoad={onImgLoad}
          onError={toggleIsShowingError}
          isClosed={isClosed}
          height={height}
          srcSet={imageSupportsWebP ? getDoubleDpiSrc(src) : src}
        />
      </picture>
    </>
  );
}

BackgroundImage.propTypes = {
  src: PropTypes.string.isRequired,
  isClosed: PropTypes.bool.isRequired,
  height: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export default BackgroundImage;
